/**
 * 대기 상태의 안내 경계. `EmptyState`(`@/shared/ui/emptyState`)의 짝이다.
 *
 * Owns:
 * - 대기 자리의 모양. `EmptyState` 를 그대로 쓰고 아이콘 자리에 스피너를 둔다. 그래서 같은 자리가
 *   대기에서 빈 상태·오류로 바뀔 때 면·여백·글자가 그대로다.
 * - 크기별 스피너 크기. `lg` 의 가운데 배치는 `EmptyState` 가 소유한다.
 * - 글자 줄 안에 직접 두는 스피너의 크기·높이 보정(`inlineSpinnerClassName`).
 * - 그 스피너와 글자를 한 줄에 두는 배치(`inlineLoadingLineClassName`).
 *
 * Does not own:
 * - 안내 문구. 소비처가 전달한다.
 * - 요청과 취소. 소비처가 소유한다.
 */
import { Spinner, type SpinnerSize } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { EmptyState, type EmptyStateProps } from "@/shared/ui/emptyState/public";
import styles from "./LoadingState.module.css";

export type LoadingStateSize = NonNullable<EmptyStateProps["size"]>;

export type LoadingStateProps = {
  // 무엇을 기다리는지 알리는 완결된 문장.
  children: ReactNode;
  // sm 은 패널 안 한 줄, md 는 패널 자리, lg 는 페이지·탭 본문 전체다. lg 는 받은 높이를 채우고
  // 가로·세로 가운데에 놓인다(EmptyState 와 같다).
  size?: LoadingStateSize;
  // 빈 상태와 같은 공간을 배경 자체가 채운다.
  fill?: EmptyStateProps["fill"];
  // 같은 자리의 EmptyState 와 같은 값을 준다. 이미 면 위에 있는 자리는 plain, sunken 바탕 위는 surface 다.
  mode?: EmptyStateProps["mode"];
};

// 자리가 커지면 스피너도 커진다. lg 는 화면 전체를 기다리는 자리라서 멀리서도 보여야 한다.
const SPINNER_SIZE: Record<LoadingStateSize, SpinnerSize> = { sm: "sm", md: "lg", lg: "xl" };

// 작은 글자 줄 안에 c6 Spinner 를 직접 둘 때 그 Spinner 의 className 으로 준다. 스피너를 둘러싼 글자
// 크기에 맞추고 한글 글자의 획 높이에 맞춰 올린다. 면이 있는 대기 자리는 LoadingState 를 쓴다.
export const inlineSpinnerClassName = styles.inlineSpinner;

// 면 없이 글자 옆에 스피너를 두는 한 줄(머리 줄 안의 대기, 좁은 카드 안의 한 줄)의 배치다. 줄을 감싸는
// 요소에 준다. 자리마다 같은 flex 세 줄을 다시 쓰지 않는다.
export const inlineLoadingLineClassName = styles.inlineLine;

export function LoadingState({ children, size = "md", mode, fill }: LoadingStateProps): ReactNode {
  // 문장이 상태를 전한다. 그래서 스피너는 장식이다(label 없음). 알림은 EmptyState 의 status 하나다.
  return (
    <EmptyState size={size} mode={mode} fill={fill} icon={<Spinner size={SPINNER_SIZE[size]} />}>
      {children}
    </EmptyState>
  );
}
